'use client'

import { InfoIcon, ShoppingCartSimpleIcon, XIcon } from '@phosphor-icons/react'
import { useCart } from '@shopify/hydrogen-react'
import { usePathname } from 'next/navigation'
import { Fragment, useState } from 'react'

import { CartLineItem } from '@/components/common/LineItem'
import { Button, ButtonLink } from '@/components/primitives/Button/Button'
import { Checkbox } from '@/components/primitives/Checkbox/Checkbox'
import { Drawer } from '@/components/primitives/Drawer/Drawer'
import { Loader } from '@/components/primitives/Loader/Loader'
import { Tooltip } from '@/components/primitives/Tooltip/Tooltip'
import { useAppContext } from '@/context/AppContext'
import { sendCartViewed } from '@/lib/analytics/Analytics.utils'
import { toCurrency } from '@/lib/formatPrice'
import { cn } from '@/lib/utils'

export const Cart = ({ className }: { className?: string }) => {
  const cart = useCart()
  const pathname = usePathname()
  const isAccountPage = pathname?.includes('/account')
  const cartTotalQuantity = cart?.totalQuantity ?? 0
  const isUpdating = cart.status !== 'idle'

  const checkoutUrl = cart?.checkoutUrl
  const cartLines = cart?.lines ?? []
  const isEmptyCart = cartLines.length === 0
  const { cartIsOpen, setCartIsOpen } = useAppContext()
  const [isTermsAndConditionsChecked, setIsTermsAndConditionsChecked] = useState(false)

  const onCartToggle = () => {
    sendCartViewed(cart)
  }

  return (
    <Drawer direction="right" open={cartIsOpen} onOpenChange={setCartIsOpen}>
      <Drawer.Trigger>
        <button
          onClick={onCartToggle}
          title="Open Cart"
          className={cn(
            'flex flex-row items-center justify-center gap-2 rounded-full',
            isAccountPage ? 'bg-overlay-dark p-3 text-white' : `h-10 border-2 p-2 text-primary-500`,
            cartTotalQuantity > 0 && !isAccountPage && 'pl-3',
            className,
          )}
        >
          <ShoppingCartSimpleIcon size={isAccountPage || cartTotalQuantity > 0 ? 24 : 20} className="currentColor" weight="bold" />
          {cartTotalQuantity > 0 && <span className="flex size-6 items-center justify-center rounded-full bg-primary-500 text-xs font-bold text-white">{cartTotalQuantity}</span>}
        </button>
      </Drawer.Trigger>
      <Drawer.Content className="no-scrollbar overflow-y-autorounded-l-2xl bg-white">
        <Drawer.Header className="flex items-center justify-between border-b-8 border-grey-100 px-3 py-4">
          <Drawer.Title className="text-heading upper font-medium">Cart</Drawer.Title>
          <Drawer.Close>
            <button title="Close">
              <XIcon size={20} />
            </button>
          </Drawer.Close>
        </Drawer.Header>
        <Drawer.Body className="flex h-full flex-col gap-6 overflow-y-auto p-4">
          {isUpdating && (
            <div className="absolute left-0 top-0 z-10 size-full place-items-center content-center bg-white/80">
              <Loader type="default" sizeClassName="size-8" />
            </div>
          )}

          {isEmptyCart ? (
            <div className="flex h-full flex-col justify-center gap-4">
              <p className="text-center text-grey-700">It looks like your cart is empty</p>
              <ButtonLink href="/" variant="outline">
                Continue Shopping
              </ButtonLink>
            </div>
          ) : (
            <>
              <div className="flex flex-col gap-4">
                {cartLines.map(
                  (line) =>
                    line && (
                      <Fragment key={line.id}>
                        <CartLineItem key={line.id} line={line} />
                        <div className="border-t border-grey-200"></div>
                      </Fragment>
                    ),
                )}
              </div>
              <div className="flex flex-col gap-6">
                {cart.cost?.subtotalAmount?.amount && (
                  <div className="flex items-center justify-between">
                    <p className="flex items-center gap-1">
                      Subtotal{' '}
                      <Tooltip content="Total before shipping, taxes, and discounts">
                        <InfoIcon size={16} />
                      </Tooltip>
                    </p>
                    <p className="font-semibold text-grey-900">{toCurrency({ value: Number(cart.cost.subtotalAmount.amount) })}</p>
                  </div>
                )}
                <Checkbox
                  id="terms-and-conditions-check"
                  label={
                    <span>
                      I agree to the {''}
                      <a href="/general-conditions" target="_blank" rel="noopener noreferrer" className="underline underline-offset-4">
                        terms and conditions
                      </a>
                    </span>
                  }
                  checked={isTermsAndConditionsChecked}
                  onChange={(checked) => setIsTermsAndConditionsChecked(checked)}
                />
                <div className="flex flex-col gap-4">
                  <ButtonLink href={checkoutUrl ?? '/'} disabled={!cartLines.length || !isTermsAndConditionsChecked}>
                    Continue to Checkout
                  </ButtonLink>
                  <Button onClick={() => setCartIsOpen(false)} variant="outline">
                    Continue Shopping
                  </Button>
                </div>
              </div>
            </>
          )}
        </Drawer.Body>
      </Drawer.Content>
    </Drawer>
  )
}
